import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import logo from '../img/ICETEX-TRANSPARENTE.png';

const DROPDOWN_ITEM = 'flex items-center bg-transparent border-0 w-full text-left cursor-pointer font-semibold no-underline gap-3 px-5 py-[14px] text-[#1a202c] text-[15px] border-b border-[#e2e8f0] transition-all duration-200 last:border-b-0 hover:bg-[#f7fafc] hover:pl-[25px] hover:text-[#1a202c]';

const DROPDOWN_ITEM_LOGOUT = 'flex items-center bg-transparent border-0 w-full text-left cursor-pointer font-semibold no-underline gap-3 px-5 py-[14px] text-[#dc3545] text-[15px] border-b border-[#e2e8f0] transition-all duration-200 last:border-b-0 hover:bg-[#fff5f5] hover:pl-[25px] hover:text-[#c82333]';

const MOODLE_SSO_URL = 'https://moodle.productividad360.com.co/login/index.php';

const NAV_LINK = (isActive) =>
  `relative no-underline font-semibold text-[15px] whitespace-nowrap transition-colors duration-300 pb-1 ${
    isActive
      ? 'text-cobalt border-b-2 border-cobalt'
      : 'text-cobalt hover:text-cobalt-hover'
  }`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen]                     = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen]             = useState(false);
  const [isDiagnosticMenuOpen, setIsDiagnosticMenuOpen] = useState(false);
  const { user, logout }  = useContext(AuthContext);
  const navigate          = useNavigate();
  const location          = useLocation();
  const userMenuRef       = useRef(null);
  const diagnosticMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setIsUserMenuOpen(false);
      if (diagnosticMenuRef.current && !diagnosticMenuRef.current.contains(event.target)) setIsDiagnosticMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => { logout(); setIsUserMenuOpen(false); setIsMenuOpen(false); navigate('/'); };
  const close = () => { setIsMenuOpen(false); setIsDiagnosticMenuOpen(false); setIsUserMenuOpen(false); };

  const isActive = (path) => location.pathname === path;
  const isAsistentesActive = location.pathname.startsWith('/plans');

  return (
    <header className="bg-white w-full sticky top-0 z-[1000] shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 h-[72px]">

        {/* Logo */}
        <Link to="/" onClick={close} className="flex-shrink-0">
          <img src={logo} alt="Logo Icetex" className="h-[50px] object-contain" />
        </Link>

        {/* Hamburger (mobile) */}
        <button
          className="hidden max-nav:flex flex-col justify-center items-center gap-[5px] bg-transparent border-0 p-2 cursor-pointer w-10 h-10"
          aria-expanded={isMenuOpen}
          aria-label="Menú de navegación"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span className={`block w-6 h-[2px] bg-cobalt transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
          <span className={`block w-6 h-[2px] bg-cobalt transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-6 h-[2px] bg-cobalt transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
        </button>

        {/* Nav + Botones: TODO en una sola fila en desktop */}
        <nav className={`${isMenuOpen
          ? 'flex max-nav:absolute max-nav:left-0 max-nav:right-0 max-nav:top-full max-nav:bg-white max-nav:shadow-[0_4px_12px_rgba(0,0,0,0.08)] max-nav:border-t max-nav:border-gray-border max-nav:py-4 max-nav:px-6 max-nav:animate-dropdown max-nav:flex-col'
          : 'max-nav:hidden'
        } nav:flex nav:items-center nav:gap-7`}>

          {/* Links de navegación */}
          <div className="flex items-center gap-7 max-nav:flex-col max-nav:items-start max-nav:gap-4">
            <Link to="/" className={NAV_LINK(isActive('/'))} onClick={close}>
              Inicio
            </Link>

            <a href={MOODLE_SSO_URL} target="_blank" rel="noopener noreferrer" className={NAV_LINK(false)} onClick={close}>
              Formación
            </a>

            {/* Asistentes IA — dropdown */}
            <div className="relative" ref={diagnosticMenuRef}>
              <button
                className={`bg-transparent border-0 font-semibold text-[15px] cursor-pointer inline-flex items-center p-0 gap-[6px] whitespace-nowrap transition-colors duration-300 pb-1 ${
                  isAsistentesActive
                    ? 'text-cobalt border-b-2 border-cobalt'
                    : 'text-cobalt hover:text-cobalt-hover'
                }`}
                onClick={() => setIsDiagnosticMenuOpen(!isDiagnosticMenuOpen)}
                aria-expanded={isDiagnosticMenuOpen}
              >
                <span>Asistentes IA</span>
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-300 ${isDiagnosticMenuOpen ? 'rotate-180' : ''}`}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {isDiagnosticMenuOpen && (
                <div className="absolute bg-white overflow-hidden z-[1000] top-[calc(100%+10px)] left-0 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] min-w-[260px] animate-dropdown border border-gray-border">
                  <Link to="/plans/opp" className={DROPDOWN_ITEM} onClick={close}>
                    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    Mapa de Oportunidades
                  </Link>
                  <Link to="/plans/bmc" className={DROPDOWN_ITEM} onClick={close}>
                    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
                    Modelo de Negocios
                  </Link>                  
                  <Link to="/plans/pmv" className={DROPDOWN_ITEM} onClick={close}>
                    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                    Validación de Hipótesis PMV
                  </Link>
                  <Link to="/plans/pitch" className={DROPDOWN_ITEM} onClick={close}>
                    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    Pitch Profesional
                  </Link>
                </div>
              )}
            </div>

            <Link to="/mentoria-ia" className={NAV_LINK(isActive('/mentoria-ia'))} onClick={close}>
              Mentoría IA
            </Link>

            <Link to="/profile/perfil" className={NAV_LINK(isActive('/profile/perfil'))} onClick={close}>
              Perfil Profesional
            </Link>
          </div>

          {/* Separador visual (solo desktop) */}
          <div className="hidden nav:block w-px h-6 bg-gray-border" />

          {/* Botones de acción */}
          <div className="flex items-center gap-3 max-nav:flex-col max-nav:w-full max-nav:mt-4 max-nav:pt-4 max-nav:border-t max-nav:border-gray-border">
            {user ? (
              <div className="relative max-nav:w-full" ref={userMenuRef}>
                <button
                  className="bg-cobalt text-white border-0 font-bold text-sm cursor-pointer inline-flex items-center px-4 py-[9px] rounded gap-2 whitespace-nowrap transition-all duration-300 hover:bg-cobalt-hover max-nav:w-full max-nav:justify-center"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-expanded={isUserMenuOpen}
                >
                  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span>{user.full_name}</span>
                  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute bg-white overflow-hidden z-[1000] top-[calc(100%+10px)] right-0 max-nav:left-0 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] min-w-[220px] animate-dropdown border border-gray-border">
                    <Link to="/change-password" className={DROPDOWN_ITEM} onClick={close}>
                      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      Cambiar Contraseña
                    </Link>
                    <button className={DROPDOWN_ITEM_LOGOUT} onClick={handleLogout}>
                      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1-2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="bg-cobalt text-white py-[9px] px-5 rounded text-sm font-bold no-underline whitespace-nowrap transition-all duration-300 hover:bg-cobalt-hover hover:shadow-md max-nav:w-full max-nav:text-center max-nav:py-3"
                  onClick={close}
                >
                  Inscríbete
                </Link>
                <Link
                  to="/login"
                  className="border-[1.5px] border-cobalt text-cobalt py-[8px] px-5 rounded text-sm font-bold no-underline whitespace-nowrap transition-all duration-300 hover:bg-cobalt hover:text-white max-nav:w-full max-nav:text-center max-nav:py-3"
                  onClick={close}
                >
                  Iniciar sesión
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
